"use client";

import React from "react"

import { useState, useRef, useCallback, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTTS } from "@/lib/use-tts";
import { useLanguage } from "@/lib/language-context";
import { SpeakerButton } from "./speaker-button";

export interface WordDefinition {
  word: string;
  translation: string;
  pronunciation?: string;
  example?: string;
}

interface WordLookupProps {
  dictionary: Map<string, WordDefinition>;
  children: ReactNode;
  className?: string;
}

interface LookupPopup {
  word: string;
  definition: WordDefinition | null;
  x: number;
  y: number;
}

export function WordLookup({ dictionary, children, className }: WordLookupProps) {
  const [popup, setPopup] = useState<LookupPopup | null>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { targetLanguage } = useLanguage();

  const normalizeWord = (word: string): string => {
    return word
      .toLowerCase()
      .replace(/[.,!?;:'"()]/g, "")
      .trim();
  };

  const findDefinition = (word: string): WordDefinition | null => {
    const normalized = normalizeWord(word);
    
    // Direct lookup
    if (dictionary.has(normalized)) {
      return dictionary.get(normalized)!;
    }
    
    // Try without common suffixes (basic stemming for Portuguese)
    const stems = [
      normalized.replace(/s$/, ""),      // plural
      normalized.replace(/mos$/, "r"),   // verb conjugation (podemos -> poder)
      normalized.replace(/ão$/, "ar"),   // verb conjugation
      normalized.replace(/em$/, "er"),   // verb conjugation
      normalized.replace(/mente$/, ""),  // adverb
    ];
    
    for (const stem of stems) {
      if (dictionary.has(stem)) {
        return dictionary.get(stem)!;
      }
    }
    
    return null;
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    
    // Check if we're touching a word span
    if (target.dataset.word) {
      const word = target.dataset.word;
      const rect = target.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      
      longPressTimer.current = setTimeout(() => {
        const definition = findDefinition(word);
        setPopup({
          word,
          definition,
          x: rect.left - (containerRect?.left || 0) + rect.width / 2,
          y: rect.top - (containerRect?.top || 0) - 10,
        });
        
        // Haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }, 500); // 500ms long press
    }
  }, [dictionary]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchMove = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const closePopup = useCallback(() => {
    setPopup(null);
  }, []);

  const { speak, stop, state: ttsState } = useTTS({ lang: targetLanguage.code });

  const handleSpeak = (text: string) => {
    if (ttsState === "playing") {
      stop();
    } else {
      speak(text);
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
    >
      {children}

      {/* Lookup Popup */}
      {popup && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={closePopup}
            onTouchStart={closePopup}
          />

          {/* Popup */}
          <div
            className="absolute z-50 w-64 bg-card border border-border rounded-lg shadow-lg p-3 transform -translate-x-1/2 -translate-y-full"
            style={{ left: popup.x, top: popup.y }}
          >
            {/* Arrow */}
            <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full">
              <div className="w-3 h-3 bg-card border-r border-b border-border transform rotate-45 -translate-y-1.5" />
            </div>

            {popup.definition ? (
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-semibold text-foreground">{popup.word}</p>
                    {popup.definition.pronunciation && (
                      <p className="text-xs text-muted-foreground">
                        /{popup.definition.pronunciation}/
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <SpeakerButton
                      state={ttsState}
                      onClick={() => handleSpeak(popup.word)}
                      iconClassName="text-muted-foreground"
                    />
                    <button
                      type="button"
                      onClick={closePopup}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted active:bg-muted/80"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-primary font-medium">
                  {popup.definition.translation}
                </p>
                
                {popup.definition.example && (
                  <p className="text-xs text-muted-foreground mt-2 italic border-l-2 border-primary/30 pl-2">
                    {popup.definition.example}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-foreground">{popup.word}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Word not found in vocabulary
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closePopup}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted active:bg-muted/80 shrink-0"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Helper to render text with tappable words
export function TappableText({ 
  text, 
  className 
}: { 
  text: string; 
  className?: string;
}) {
  const words = text.split(/(\s+)/);
  
  return (
    <span className={className}>
      {words.map((word, index) => {
        // Keep whitespace as-is
        if (/^\s+$/.test(word)) {
          return <span key={index}>{word}</span>;
        }
        
        // Make words tappable
        return (
          <span
            key={index}
            data-word={word}
            className="cursor-pointer select-none active:bg-primary/20 rounded px-0.5 -mx-0.5"
          >
            {word}
          </span>
        );
      })}
    </span>
  );
}

// Build dictionary from the new TopicVocabulary structure
export function buildDictionary(vocabulary: {
  words?: Array<{
    baseForm: string;
    translation: string;
    pronunciation?: string;
    forms: Array<{
      form: string;
      usage: string;
      example?: { sentence: string; translation: string };
      context?: { before?: string; line: string; after?: string; situation: string };
    }>;
    examples: Array<{ text: string; translation: string }>;
  }>;
  expressions?: Array<{
    expression: string;
    translation: string;
    literal?: string;
    components?: Array<{ word: string; meaning: string; note?: string }>;
    examples: Array<{ text: string; translation: string }> | string[];
  }>;
  topical?: Array<{
    category: string;
    items: Array<{
      word: string;
      translation: string;
      pronunciation?: string;
    }>;
  }>;
}): Map<string, WordDefinition> {
  const dict = new Map<string, WordDefinition>();

  // Add words with all their forms
  if (vocabulary.words) {
    for (const entry of vocabulary.words) {
      if (!entry.baseForm) continue;
      const exampleText = entry.examples?.[0]?.text || "";
      const definition: WordDefinition = {
        word: entry.baseForm,
        translation: entry.translation,
        pronunciation: entry.pronunciation,
        example: exampleText,
      };

      // Add base form
      const normalizedBase = entry.baseForm.toLowerCase().replace(/[.,!?;:'"()]/g, "").trim();
      dict.set(normalizedBase, definition);

      // Add all linked forms pointing to their specific definitions
      if (Array.isArray(entry.forms)) {
        for (const formEntry of entry.forms) {
          if (!formEntry.form) continue;
          const normalizedForm = formEntry.form.toLowerCase().replace(/[.,!?;:'"()-]/g, "").trim();
          if (!dict.has(normalizedForm)) {
            // Use form's own example if available, otherwise fall back to word's example
            const formExample = formEntry.example?.sentence || formEntry.context?.line || exampleText;
            const formDefinition: WordDefinition = {
              word: `${formEntry.form} (${entry.baseForm})`,
              translation: formEntry.usage || entry.translation,
              pronunciation: entry.pronunciation,
              example: formExample,
            };
            dict.set(normalizedForm, formDefinition);
          }
        }
      }
    }
  }

  // Add expressions
  if (vocabulary.expressions) {
    for (const entry of vocabulary.expressions) {
      if (!entry.expression) continue;
      // Handle both old string[] format and new {text, translation}[] format
      const firstExample = entry.examples?.[0];
      const exampleText = !firstExample ? "" : typeof firstExample === 'string' ? firstExample : firstExample?.text || "";

      const definition: WordDefinition = {
        word: entry.expression,
        translation: entry.translation,
        example: exampleText,
      };

      const normalized = entry.expression.toLowerCase().replace(/[.,!?;:'"()]/g, "").trim();
      dict.set(normalized, definition);

      // Add component words with their individual meanings
      if (entry.components) {
        for (const comp of entry.components) {
          if (!comp.word) continue;
          const normalizedComp = comp.word.toLowerCase().replace(/[.,!?;:'"()-]/g, "").trim();
          if (!dict.has(normalizedComp)) {
            const compDefinition: WordDefinition = {
              word: comp.word,
              translation: comp.meaning + (comp.note ? ` (${comp.note})` : ""),
              example: `Part of: "${entry.expression}" = ${entry.translation}`,
            };
            dict.set(normalizedComp, compDefinition);
          }
        }
      }

      // Also add individual words for multi-word expressions (fallback if no components)
      if (!entry.components) {
        const parts = normalized.split(/\s+/);
        if (parts.length > 1) {
          for (const part of parts) {
            if (!dict.has(part) && part.length > 2) {
              dict.set(part, definition);
            }
          }
        }
      }
    }
  }

  // Add topical vocabulary
  if (vocabulary.topical) {
    for (const category of vocabulary.topical) {
      if (!category.items) continue;
      for (const item of category.items) {
        if (!item.word) continue;
        const definition: WordDefinition = {
          word: item.word,
          translation: item.translation,
          pronunciation: item.pronunciation,
        };

        const normalized = item.word.toLowerCase().replace(/[.,!?;:'"()]/g, "").trim();
        dict.set(normalized, definition);

        // Also add individual words for multi-word entries like "em frente"
        const parts = normalized.split(/\s+/);
        if (parts.length > 1) {
          for (const part of parts) {
            if (!dict.has(part) && part.length > 2) {
              dict.set(part, definition);
            }
          }
        }
      }
    }
  }

  return dict;
}
